/**
 * SLAP BATTLES - Hype Landing Page
 * THE GATES ARE CLOSED - Countdown to Launch
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Sparkles, Swords, Coins, Zap, ExternalLink, Volume2, VolumeX, Wallet, RefreshCw, Shield, CreditCard } from 'lucide-react';

// Target: 15:00 UTC on February 17, 2026 (01:00 + 14 hours)
const TARGET_LAUNCH_TIME = Date.UTC(2026, 1, 17, 15, 0, 0); // Month is 0-indexed (1 = February)

// Preview screenshots
const PREVIEW_IMAGES = [
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/acaa3bfe-fab4-49af-9037-3e2a2d67c830/images/4738dfea81de1e22f9f3d2aa87ad7c93fd99ea26c302b7db2e1932ed2f959716.png',
    title: 'COLLECTIBLE CARDS',
    description: 'Collect rare NFT cards'
  },
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/acaa3bfe-fab4-49af-9037-3e2a2d67c830/images/ba128902f3ac1bde8e12163d6142fdc276fba0b84c335546abd53b8d23654ebb.png',
    title: 'PVP BATTLES',
    description: 'Fight against other players'
  },
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/acaa3bfe-fab4-49af-9037-3e2a2d67c830/images/8e4b15635c202fe54bf269bad3bdf05c5a6e516b40deb7b543094346edb4449a.png',
    title: 'CRYPTO WALLET',
    description: 'Your own Solana wallet'
  }
];

// Translations for all supported languages
const TRANSLATIONS = {
  en: {
    flag: '🇺🇸',
    name: 'English',
    locked: 'THE ARENA IS LOCKED',
    subtitle: '$SLAP TOKEN LAUNCH FIRST. GAME ACCESS FOLLOWS.',
    days: 'Days', hours: 'Hours', mins: 'Mins', secs: 'Secs',
    packOpening: 'PACK OPENING', packDesc: 'Draw legendary cards with $SLAP.',
    pvp: 'MULTIPLAYER PVP', pvpDesc: 'Battle live against other Degens.',
    earn: 'EARN SOLANA', earnDesc: 'Every slap brings you closer to the jackpot.',
    sneakPeek: 'SNEAK PEEK',
    cards: 'COLLECTIBLE CARDS', cardsDesc: 'Collect rare NFT cards',
    battles: 'PVP BATTLES', battlesDesc: 'Fight against other players',
    wallet: 'CRYPTO WALLET', walletDesc: 'Your own Solana wallet',
    features: 'FEATURES',
    ownWallet: 'OWN WALLET', ownWalletDesc: 'Your own Solana wallet is created automatically. No external wallet needed!',
    instantSwap: 'INSTANT SWAP', instantSwapDesc: 'Swap SOL for $SLAP directly in the app. Fast & easy.',
    pinProtect: 'PIN PROTECTION', pinProtectDesc: 'Secure your account with a 4-digit PIN. Your keys, your coins.',
    startFrom: 'FROM 0.02 SOL', startFromDesc: 'Start from just ~$3. Perfect for testing!',
    buySlap: 'BUY $SLAP ON PUMP.FUN',
    selectLang: 'Select Language'
  },
  de: {
    flag: '🇩🇪',
    name: 'Deutsch',
    locked: 'DIE ARENA IST GESPERRT',
    subtitle: '$SLAP TOKEN LAUNCH ZUERST. SPIELZUGANG FOLGT.',
    days: 'Tage', hours: 'Stunden', mins: 'Min', secs: 'Sek',
    packOpening: 'PACK ÖFFNEN', packDesc: 'Ziehe legendäre Karten mit $SLAP.',
    pvp: 'MULTIPLAYER PVP', pvpDesc: 'Kämpfe live gegen andere Degens.',
    earn: 'VERDIENE SOLANA', earnDesc: 'Jeder Slap bringt dich näher zum Jackpot.',
    sneakPeek: 'VORSCHAU',
    cards: 'SAMMELKARTEN', cardsDesc: 'Sammle seltene NFT-Karten',
    battles: 'PVP KÄMPFE', battlesDesc: 'Kämpfe gegen andere Spieler',
    wallet: 'CRYPTO WALLET', walletDesc: 'Deine eigene Solana Wallet',
    features: 'FEATURES',
    ownWallet: 'EIGENE WALLET', ownWalletDesc: 'Deine eigene Solana Wallet wird automatisch erstellt. Kein externes Wallet nötig!',
    instantSwap: 'INSTANT SWAP', instantSwapDesc: 'Tausche SOL gegen $SLAP direkt in der App. Schnell & einfach.',
    pinProtect: 'PIN SCHUTZ', pinProtectDesc: 'Sichere dein Konto mit einem 4-stelligen PIN. Deine Keys, deine Coins.',
    startFrom: 'AB 0.02 SOL', startFromDesc: 'Starte schon ab ~$3. Perfekt zum Testen!',
    buySlap: 'KAUFE $SLAP AUF PUMP.FUN',
    selectLang: 'Sprache wählen'
  },
  es: {
    flag: '🇪🇸',
    name: 'Español',
    locked: 'LA ARENA ESTÁ BLOQUEADA',
    subtitle: 'LANZAMIENTO DE $SLAP PRIMERO. ACCESO AL JUEGO DESPUÉS.',
    days: 'Días', hours: 'Horas', mins: 'Min', secs: 'Seg',
    packOpening: 'ABRIR PACKS', packDesc: 'Consigue cartas legendarias con $SLAP.',
    pvp: 'PVP MULTIJUGADOR', pvpDesc: 'Batalla en vivo contra otros Degens.',
    earn: 'GANA SOLANA', earnDesc: 'Cada slap te acerca al jackpot.',
    sneakPeek: 'ADELANTO',
    cards: 'CARTAS COLECCIONABLES', cardsDesc: 'Colecciona cartas NFT raras',
    battles: 'BATALLAS PVP', battlesDesc: 'Lucha contra otros jugadores',
    wallet: 'CRYPTO WALLET', walletDesc: 'Tu propia wallet de Solana',
    features: 'CARACTERÍSTICAS',
    ownWallet: 'WALLET PROPIA', ownWalletDesc: 'Tu wallet de Solana se crea automáticamente. ¡Sin wallet externo!',
    instantSwap: 'SWAP INSTANTÁNEO', instantSwapDesc: 'Intercambia SOL por $SLAP directamente en la app.',
    pinProtect: 'PROTECCIÓN PIN', pinProtectDesc: 'Asegura tu cuenta con un PIN de 4 dígitos.',
    startFrom: 'DESDE 0.02 SOL', startFromDesc: '¡Comienza desde ~$3. Perfecto para probar!',
    buySlap: 'COMPRA $SLAP EN PUMP.FUN',
    selectLang: 'Seleccionar idioma'
  },
  fr: {
    flag: '🇫🇷',
    name: 'Français',
    locked: "L'ARÈNE EST VERROUILLÉE",
    subtitle: 'LANCEMENT DU TOKEN $SLAP EN PREMIER. ACCÈS AU JEU ENSUITE.',
    days: 'Jours', hours: 'Heures', mins: 'Min', secs: 'Sec',
    packOpening: 'OUVRIR DES PACKS', packDesc: 'Obtenez des cartes légendaires avec $SLAP.',
    pvp: 'PVP MULTIJOUEUR', pvpDesc: 'Combattez en direct contre d\'autres Degens.',
    earn: 'GAGNEZ DU SOLANA', earnDesc: 'Chaque slap vous rapproche du jackpot.',
    sneakPeek: 'APERÇU',
    cards: 'CARTES À COLLECTIONNER', cardsDesc: 'Collectionnez des cartes NFT rares',
    battles: 'COMBATS PVP', battlesDesc: 'Affrontez d\'autres joueurs',
    wallet: 'PORTEFEUILLE CRYPTO', walletDesc: 'Votre propre portefeuille Solana',
    features: 'FONCTIONNALITÉS',
    ownWallet: 'PORTEFEUILLE PROPRE', ownWalletDesc: 'Votre portefeuille Solana est créé automatiquement. Pas de portefeuille externe!',
    instantSwap: 'SWAP INSTANTANÉ', instantSwapDesc: 'Échangez SOL contre $SLAP directement dans l\'app.',
    pinProtect: 'PROTECTION PIN', pinProtectDesc: 'Sécurisez votre compte avec un PIN à 4 chiffres.',
    startFrom: 'À PARTIR DE 0.02 SOL', startFromDesc: 'Commencez dès ~$3. Parfait pour tester!',
    buySlap: 'ACHETEZ $SLAP SUR PUMP.FUN',
    selectLang: 'Choisir la langue'
  },
  pt: {
    flag: '🇧🇷',
    name: 'Português',
    locked: 'A ARENA ESTÁ BLOQUEADA',
    subtitle: 'LANÇAMENTO DO TOKEN $SLAP PRIMEIRO. ACESSO AO JOGO DEPOIS.',
    days: 'Dias', hours: 'Horas', mins: 'Min', secs: 'Seg',
    packOpening: 'ABRIR PACKS', packDesc: 'Ganhe cartas lendárias com $SLAP.',
    pvp: 'PVP MULTIPLAYER', pvpDesc: 'Batalhe ao vivo contra outros Degens.',
    earn: 'GANHE SOLANA', earnDesc: 'Cada slap te aproxima do jackpot.',
    sneakPeek: 'PRÉVIA',
    cards: 'CARTAS COLECIONÁVEIS', cardsDesc: 'Colecione cartas NFT raras',
    battles: 'BATALHAS PVP', battlesDesc: 'Lute contra outros jogadores',
    wallet: 'CARTEIRA CRYPTO', walletDesc: 'Sua própria carteira Solana',
    features: 'RECURSOS',
    ownWallet: 'CARTEIRA PRÓPRIA', ownWalletDesc: 'Sua carteira Solana é criada automaticamente. Sem carteira externa!',
    instantSwap: 'SWAP INSTANTÂNEO', instantSwapDesc: 'Troque SOL por $SLAP diretamente no app.',
    pinProtect: 'PROTEÇÃO PIN', pinProtectDesc: 'Proteja sua conta com um PIN de 4 dígitos.',
    startFrom: 'A PARTIR DE 0.02 SOL', startFromDesc: 'Comece a partir de ~$3. Perfeito para testar!',
    buySlap: 'COMPRE $SLAP NA PUMP.FUN',
    selectLang: 'Selecionar idioma'
  },
  ru: {
    flag: '🇷🇺',
    name: 'Русский',
    locked: 'АРЕНА ЗАБЛОКИРОВАНА',
    subtitle: 'СНАЧАЛА ЗАПУСК ТОКЕНА $SLAP. ДОСТУП К ИГРЕ ПОТОМ.',
    days: 'Дни', hours: 'Часы', mins: 'Мин', secs: 'Сек',
    packOpening: 'ОТКРЫТИЕ ПАКОВ', packDesc: 'Получайте легендарные карты за $SLAP.',
    pvp: 'МУЛЬТИПЛЕЕР PVP', pvpDesc: 'Сражайтесь онлайн с другими Дегенами.',
    earn: 'ЗАРАБАТЫВАЙ SOLANA', earnDesc: 'Каждый слэп приближает тебя к джекпоту.',
    sneakPeek: 'ПРЕВЬЮ',
    cards: 'КОЛЛЕКЦИОННЫЕ КАРТЫ', cardsDesc: 'Собирайте редкие NFT карты',
    battles: 'PVP СРАЖЕНИЯ', battlesDesc: 'Сражайтесь с другими игроками',
    wallet: 'КРИПТО КОШЕЛЕК', walletDesc: 'Ваш собственный кошелек Solana',
    features: 'ФУНКЦИИ',
    ownWallet: 'СВОЙ КОШЕЛЕК', ownWalletDesc: 'Ваш кошелек Solana создается автоматически. Внешний кошелек не нужен!',
    instantSwap: 'МГНОВЕННЫЙ ОБМЕН', instantSwapDesc: 'Обменивайте SOL на $SLAP прямо в приложении.',
    pinProtect: 'PIN ЗАЩИТА', pinProtectDesc: 'Защитите аккаунт 4-значным PIN-кодом.',
    startFrom: 'ОТ 0.02 SOL', startFromDesc: 'Начните всего с ~$3. Идеально для теста!',
    buySlap: 'КУПИТЬ $SLAP НА PUMP.FUN',
    selectLang: 'Выбрать язык'
  },
  zh: {
    flag: '🇨🇳',
    name: '中文',
    locked: '竞技场已锁定',
    subtitle: '$SLAP代币先发布。游戏访问随后。',
    days: '天', hours: '小时', mins: '分', secs: '秒',
    packOpening: '开包', packDesc: '用$SLAP抽取传奇卡牌。',
    pvp: '多人PVP', pvpDesc: '与其他玩家实时对战。',
    earn: '赚取SOLANA', earnDesc: '每次拍打都让你更接近大奖。',
    sneakPeek: '预览',
    cards: '收藏卡', cardsDesc: '收集稀有NFT卡牌',
    battles: 'PVP战斗', battlesDesc: '与其他玩家对战',
    wallet: '加密钱包', walletDesc: '你自己的Solana钱包',
    features: '功能',
    ownWallet: '自有钱包', ownWalletDesc: '自动创建你的Solana钱包。无需外部钱包！',
    instantSwap: '即时兑换', instantSwapDesc: '直接在应用内将SOL兑换为$SLAP。',
    pinProtect: 'PIN保护', pinProtectDesc: '用4位PIN码保护你的账户。',
    startFrom: '从0.02 SOL起', startFromDesc: '从约$3开始。非常适合测试！',
    buySlap: '在PUMP.FUN购买$SLAP',
    selectLang: '选择语言'
  },
  ja: {
    flag: '🇯🇵',
    name: '日本語',
    locked: 'アリーナはロック中',
    subtitle: '$SLAPトークンが先にローンチ。ゲームアクセスは後。',
    days: '日', hours: '時間', mins: '分', secs: '秒',
    packOpening: 'パック開封', packDesc: '$SLAPでレジェンドカードを引こう。',
    pvp: 'マルチプレイヤーPVP', pvpDesc: '他のDegenとリアルタイムバトル。',
    earn: 'SOLANAを稼ぐ', earnDesc: 'スラップするたびにジャックポットに近づく。',
    sneakPeek: 'プレビュー',
    cards: 'コレクタブルカード', cardsDesc: 'レアNFTカードを集めよう',
    battles: 'PVPバトル', battlesDesc: '他のプレイヤーと戦う',
    wallet: 'クリプトウォレット', walletDesc: 'あなた専用のSolanaウォレット',
    features: '機能',
    ownWallet: '専用ウォレット', ownWalletDesc: 'Solanaウォレットが自動作成。外部ウォレット不要！',
    instantSwap: '即時スワップ', instantSwapDesc: 'アプリ内でSOLを$SLAPに直接交換。',
    pinProtect: 'PIN保護', pinProtectDesc: '4桁のPINでアカウントを保護。',
    startFrom: '0.02 SOLから', startFromDesc: '約$3から開始。テストに最適！',
    buySlap: 'PUMP.FUNで$SLAPを購入',
    selectLang: '言語を選択'
  },
  ko: {
    flag: '🇰🇷',
    name: '한국어',
    locked: '아레나가 잠겨 있습니다',
    subtitle: '$SLAP 토큰 출시가 먼저. 게임 접근은 나중에.',
    days: '일', hours: '시간', mins: '분', secs: '초',
    packOpening: '팩 오픈', packDesc: '$SLAP으로 전설 카드를 뽑으세요.',
    pvp: '멀티플레이어 PVP', pvpDesc: '다른 Degen과 실시간 배틀.',
    earn: 'SOLANA 벌기', earnDesc: '매 슬랩이 잭팟에 가까워집니다.',
    sneakPeek: '미리보기',
    cards: '수집 카드', cardsDesc: '희귀 NFT 카드 수집',
    battles: 'PVP 배틀', battlesDesc: '다른 플레이어와 싸우기',
    wallet: '크립토 지갑', walletDesc: '당신만의 Solana 지갑',
    features: '기능',
    ownWallet: '개인 지갑', ownWalletDesc: 'Solana 지갑이 자동 생성됩니다. 외부 지갑 불필요!',
    instantSwap: '즉시 스왑', instantSwapDesc: '앱에서 직접 SOL을 $SLAP으로 교환.',
    pinProtect: 'PIN 보호', pinProtectDesc: '4자리 PIN으로 계정 보호.',
    startFrom: '0.02 SOL부터', startFromDesc: '~$3부터 시작. 테스트에 완벽!',
    buySlap: 'PUMP.FUN에서 $SLAP 구매',
    selectLang: '언어 선택'
  },
  tr: {
    flag: '🇹🇷',
    name: 'Türkçe',
    locked: 'ARENA KİLİTLİ',
    subtitle: 'ÖNCE $SLAP TOKEN LANSMANI. OYUN ERİŞİMİ SONRA.',
    days: 'Gün', hours: 'Saat', mins: 'Dk', secs: 'Sn',
    packOpening: 'PAKET AÇ', packDesc: '$SLAP ile efsanevi kartlar çek.',
    pvp: 'ÇOKLU OYUNCU PVP', pvpDesc: 'Diğer Degenlerle canlı savaş.',
    earn: 'SOLANA KAZAN', earnDesc: 'Her tokat seni jackpota yaklaştırır.',
    sneakPeek: 'ÖN İZLEME',
    cards: 'KOLEKSİYON KARTLARI', cardsDesc: 'Nadir NFT kartları topla',
    battles: 'PVP SAVAŞLARI', battlesDesc: 'Diğer oyuncularla savaş',
    wallet: 'KRİPTO CÜZDAN', walletDesc: 'Kendi Solana cüzdanın',
    features: 'ÖZELLİKLER',
    ownWallet: 'KENDİ CÜZDANIN', ownWalletDesc: 'Solana cüzdanın otomatik oluşturulur. Harici cüzdan gerekmez!',
    instantSwap: 'ANLIK SWAP', instantSwapDesc: 'SOL\'u $SLAP\'a doğrudan uygulamada değiştir.',
    pinProtect: 'PIN KORUMASI', pinProtectDesc: '4 haneli PIN ile hesabını koru.',
    startFrom: '0.02 SOL\'DAN', startFromDesc: '~$3\'dan başla. Test için mükemmel!',
    buySlap: 'PUMP.FUN\'DA $SLAP AL',
    selectLang: 'Dil seç'
  },
  ar: {
    flag: '🇸🇦',
    name: 'العربية',
    locked: 'الساحة مغلقة',
    subtitle: 'إطلاق رمز $SLAP أولاً. الوصول للعبة لاحقاً.',
    days: 'أيام', hours: 'ساعات', mins: 'دقائق', secs: 'ثواني',
    packOpening: 'فتح الحزم', packDesc: 'احصل على بطاقات أسطورية بـ $SLAP.',
    pvp: 'PVP متعدد اللاعبين', pvpDesc: 'قاتل مباشرة ضد Degens آخرين.',
    earn: 'اربح SOLANA', earnDesc: 'كل صفعة تقربك من الجائزة الكبرى.',
    sneakPeek: 'نظرة خاطفة',
    cards: 'بطاقات قابلة للجمع', cardsDesc: 'اجمع بطاقات NFT نادرة',
    battles: 'معارك PVP', battlesDesc: 'قاتل لاعبين آخرين',
    wallet: 'محفظة كريبتو', walletDesc: 'محفظة Solana الخاصة بك',
    features: 'الميزات',
    ownWallet: 'محفظة خاصة', ownWalletDesc: 'يتم إنشاء محفظة Solana تلقائياً. لا حاجة لمحفظة خارجية!',
    instantSwap: 'تبادل فوري', instantSwapDesc: 'استبدل SOL بـ $SLAP مباشرة في التطبيق.',
    pinProtect: 'حماية PIN', pinProtectDesc: 'أمّن حسابك برمز PIN من 4 أرقام.',
    startFrom: 'من 0.02 SOL', startFromDesc: 'ابدأ من ~$3 فقط. مثالي للاختبار!',
    buySlap: 'اشترِ $SLAP على PUMP.FUN',
    selectLang: 'اختر اللغة'
  },
  hi: {
    flag: '🇮🇳',
    name: 'हिंदी',
    locked: 'एरीना लॉक है',
    subtitle: '$SLAP टोकन लॉन्च पहले। गेम एक्सेस बाद में।',
    days: 'दिन', hours: 'घंटे', mins: 'मिनट', secs: 'सेकंड',
    packOpening: 'पैक खोलें', packDesc: '$SLAP से लीजेंडरी कार्ड पाएं।',
    pvp: 'मल्टीप्लेयर PVP', pvpDesc: 'अन्य Degens के खिलाफ लाइव लड़ाई।',
    earn: 'SOLANA कमाएं', earnDesc: 'हर थप्पड़ आपको जैकपॉट के करीब लाता है।',
    sneakPeek: 'झलक',
    cards: 'कलेक्टिबल कार्ड्स', cardsDesc: 'दुर्लभ NFT कार्ड इकट्ठा करें',
    battles: 'PVP बैटल्स', battlesDesc: 'अन्य खिलाड़ियों से लड़ें',
    wallet: 'क्रिप्टो वॉलेट', walletDesc: 'आपका अपना Solana वॉलेट',
    features: 'फीचर्स',
    ownWallet: 'अपना वॉलेट', ownWalletDesc: 'आपका Solana वॉलेट स्वचालित रूप से बनता है। बाहरी वॉलेट की जरूरत नहीं!',
    instantSwap: 'इंस्टेंट स्वैप', instantSwapDesc: 'ऐप में सीधे SOL को $SLAP में बदलें।',
    pinProtect: 'PIN सुरक्षा', pinProtectDesc: '4-अंकों के PIN से अपना खाता सुरक्षित करें।',
    startFrom: '0.02 SOL से', startFromDesc: '~$3 से शुरू करें। टेस्टिंग के लिए परफेक्ट!',
    buySlap: 'PUMP.FUN पर $SLAP खरीदें',
    selectLang: 'भाषा चुनें'
  },
  it: {
    flag: '🇮🇹',
    name: 'Italiano',
    locked: "L'ARENA È BLOCCATA",
    subtitle: 'LANCIO DEL TOKEN $SLAP PRIMA. ACCESSO AL GIOCO DOPO.',
    days: 'Giorni', hours: 'Ore', mins: 'Min', secs: 'Sec',
    packOpening: 'APRI PACCHI', packDesc: 'Ottieni carte leggendarie con $SLAP.',
    pvp: 'PVP MULTIPLAYER', pvpDesc: 'Combatti dal vivo contro altri Degens.',
    earn: 'GUADAGNA SOLANA', earnDesc: 'Ogni slap ti avvicina al jackpot.',
    sneakPeek: 'ANTEPRIMA',
    cards: 'CARTE COLLEZIONABILI', cardsDesc: 'Colleziona carte NFT rare',
    battles: 'BATTAGLIE PVP', battlesDesc: 'Combatti contro altri giocatori',
    wallet: 'WALLET CRYPTO', walletDesc: 'Il tuo wallet Solana personale',
    features: 'FUNZIONALITÀ',
    ownWallet: 'WALLET PROPRIO', ownWalletDesc: 'Il tuo wallet Solana viene creato automaticamente. Nessun wallet esterno!',
    instantSwap: 'SWAP ISTANTANEO', instantSwapDesc: 'Scambia SOL per $SLAP direttamente nell\'app.',
    pinProtect: 'PROTEZIONE PIN', pinProtectDesc: 'Proteggi il tuo account con un PIN a 4 cifre.',
    startFrom: 'DA 0.02 SOL', startFromDesc: 'Inizia da ~$3. Perfetto per testare!',
    buySlap: 'COMPRA $SLAP SU PUMP.FUN',
    selectLang: 'Seleziona lingua'
  },
  pl: {
    flag: '🇵🇱',
    name: 'Polski',
    locked: 'ARENA JEST ZABLOKOWANA',
    subtitle: 'NAJPIERW LAUNCH TOKENA $SLAP. DOSTĘP DO GRY PÓŹNIEJ.',
    days: 'Dni', hours: 'Godziny', mins: 'Min', secs: 'Sek',
    packOpening: 'OTWÓRZ PACZKI', packDesc: 'Zdobądź legendarne karty za $SLAP.',
    pvp: 'PVP WIELOOSOBOWE', pvpDesc: 'Walcz na żywo z innymi Degenami.',
    earn: 'ZARABIAJ SOLANA', earnDesc: 'Każdy slap przybliża cię do jackpota.',
    sneakPeek: 'PODGLĄD',
    cards: 'KARTY KOLEKCJONERSKIE', cardsDesc: 'Zbieraj rzadkie karty NFT',
    battles: 'BITWY PVP', battlesDesc: 'Walcz z innymi graczami',
    wallet: 'PORTFEL KRYPTO', walletDesc: 'Twój własny portfel Solana',
    features: 'FUNKCJE',
    ownWallet: 'WŁASNY PORTFEL', ownWalletDesc: 'Twój portfel Solana jest tworzony automatycznie. Bez zewnętrznego portfela!',
    instantSwap: 'NATYCHMIASTOWY SWAP', instantSwapDesc: 'Wymieniaj SOL na $SLAP bezpośrednio w aplikacji.',
    pinProtect: 'OCHRONA PIN', pinProtectDesc: 'Zabezpiecz konto 4-cyfrowym PIN-em.',
    startFrom: 'OD 0.02 SOL', startFromDesc: 'Zacznij od ~$3. Idealny do testów!',
    buySlap: 'KUP $SLAP NA PUMP.FUN',
    selectLang: 'Wybierz język'
  },
  nl: {
    flag: '🇳🇱',
    name: 'Nederlands',
    locked: 'DE ARENA IS VERGRENDELD',
    subtitle: '$SLAP TOKEN LAUNCH EERST. GAME TOEGANG VOLGT.',
    days: 'Dagen', hours: 'Uren', mins: 'Min', secs: 'Sec',
    packOpening: 'PACKS OPENEN', packDesc: 'Trek legendarische kaarten met $SLAP.',
    pvp: 'MULTIPLAYER PVP', pvpDesc: 'Vecht live tegen andere Degens.',
    earn: 'VERDIEN SOLANA', earnDesc: 'Elke slap brengt je dichter bij de jackpot.',
    sneakPeek: 'VOORPROEFJE',
    cards: 'VERZAMELKAARTEN', cardsDesc: 'Verzamel zeldzame NFT kaarten',
    battles: 'PVP GEVECHTEN', battlesDesc: 'Vecht tegen andere spelers',
    wallet: 'CRYPTO WALLET', walletDesc: 'Je eigen Solana wallet',
    features: 'FUNCTIES',
    ownWallet: 'EIGEN WALLET', ownWalletDesc: 'Je Solana wallet wordt automatisch aangemaakt. Geen externe wallet nodig!',
    instantSwap: 'INSTANT SWAP', instantSwapDesc: 'Wissel SOL voor $SLAP direct in de app.',
    pinProtect: 'PIN BESCHERMING', pinProtectDesc: 'Beveilig je account met een 4-cijferige PIN.',
    startFrom: 'VANAF 0.02 SOL', startFromDesc: 'Begin vanaf ~$3. Perfect om te testen!',
    buySlap: 'KOOP $SLAP OP PUMP.FUN',
    selectLang: 'Selecteer taal'
  },
  vi: {
    flag: '🇻🇳',
    name: 'Tiếng Việt',
    locked: 'ĐẤU TRƯỜNG ĐÃ KHÓA',
    subtitle: 'RA MẮT TOKEN $SLAP TRƯỚC. TRUY CẬP GAME SAU.',
    days: 'Ngày', hours: 'Giờ', mins: 'Phút', secs: 'Giây',
    packOpening: 'MỞ GÓI', packDesc: 'Nhận thẻ huyền thoại với $SLAP.',
    pvp: 'PVP NHIỀU NGƯỜI', pvpDesc: 'Chiến đấu trực tiếp với Degens khác.',
    earn: 'KIẾM SOLANA', earnDesc: 'Mỗi cú tát đưa bạn gần hơn jackpot.',
    sneakPeek: 'XEM TRƯỚC',
    cards: 'THẺ SƯU TẬP', cardsDesc: 'Sưu tập thẻ NFT hiếm',
    battles: 'TRẬN ĐẤU PVP', battlesDesc: 'Đấu với người chơi khác',
    wallet: 'VÍ CRYPTO', walletDesc: 'Ví Solana của riêng bạn',
    features: 'TÍNH NĂNG',
    ownWallet: 'VÍ RIÊNG', ownWalletDesc: 'Ví Solana của bạn được tạo tự động. Không cần ví bên ngoài!',
    instantSwap: 'SWAP NGAY', instantSwapDesc: 'Đổi SOL lấy $SLAP trực tiếp trong app.',
    pinProtect: 'BẢO VỆ PIN', pinProtectDesc: 'Bảo mật tài khoản bằng PIN 4 số.',
    startFrom: 'TỪ 0.02 SOL', startFromDesc: 'Bắt đầu từ ~$3. Hoàn hảo để thử!',
    buySlap: 'MUA $SLAP TRÊN PUMP.FUN',
    selectLang: 'Chọn ngôn ngữ'
  },
  th: {
    flag: '🇹🇭',
    name: 'ไทย',
    locked: 'อารีน่าถูกล็อค',
    subtitle: 'เปิดตัวโทเค็น $SLAP ก่อน เข้าถึงเกมทีหลัง',
    days: 'วัน', hours: 'ชั่วโมง', mins: 'นาที', secs: 'วินาที',
    packOpening: 'เปิดแพ็ค', packDesc: 'รับการ์ดตำนานด้วย $SLAP',
    pvp: 'PVP หลายคน', pvpDesc: 'ต่อสู้สดกับ Degens คนอื่น',
    earn: 'รับ SOLANA', earnDesc: 'ทุกการตบพาคุณใกล้แจ็คพอตมากขึ้น',
    sneakPeek: 'ตัวอย่าง',
    cards: 'การ์ดสะสม', cardsDesc: 'สะสมการ์ด NFT หายาก',
    battles: 'การต่อสู้ PVP', battlesDesc: 'สู้กับผู้เล่นคนอื่น',
    wallet: 'กระเป๋าคริปโต', walletDesc: 'กระเป๋า Solana ของคุณเอง',
    features: 'คุณสมบัติ',
    ownWallet: 'กระเป๋าของตัวเอง', ownWalletDesc: 'กระเป๋า Solana ถูกสร้างอัตโนมัติ ไม่ต้องใช้กระเป๋าภายนอก!',
    instantSwap: 'แลกทันที', instantSwapDesc: 'แลก SOL เป็น $SLAP โดยตรงในแอป',
    pinProtect: 'ป้องกันด้วย PIN', pinProtectDesc: 'รักษาบัญชีด้วย PIN 4 หลัก',
    startFrom: 'เริ่มที่ 0.02 SOL', startFromDesc: 'เริ่มจาก ~$3 เหมาะสำหรับทดสอบ!',
    buySlap: 'ซื้อ $SLAP บน PUMP.FUN',
    selectLang: 'เลือกภาษา'
  }
};

// Language list for selector
const LANGUAGES = Object.entries(TRANSLATIONS).map(([code, lang]) => ({
  code,
  flag: lang.flag,
  name: lang.name
}));

// Key features with icons
const KEY_FEATURES = [
  { icon: Wallet, key: 'ownWallet', descKey: 'ownWalletDesc', color: 'cyan' },
  { icon: RefreshCw, key: 'instantSwap', descKey: 'instantSwapDesc', color: 'green' },
  { icon: Shield, key: 'pinProtect', descKey: 'pinProtectDesc', color: 'purple' },
  { icon: CreditCard, key: 'startFrom', descKey: 'startFromDesc', color: 'yellow' }
];

// Feature teaser cards
const FEATURES = [
  {
    icon: '🎴',
    title: 'PACK OPENING',
    description: 'Draw legendary cards with $SLAP.',
    gradient: 'from-purple-600 to-pink-600',
    glow: 'purple'
  },
  {
    icon: '⚔️',
    title: 'MULTIPLAYER PVP',
    description: 'Battle live against other Degens.',
    gradient: 'from-red-600 to-orange-600',
    glow: 'red'
  },
  {
    icon: '💰',
    title: 'EARN SOLANA',
    description: 'Every slap brings you closer to the jackpot.',
    gradient: 'from-yellow-500 to-green-500',
    glow: 'yellow'
  }
];

// Countdown digit component
const CountdownDigit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="relative">
      <div className="bg-black/80 border-2 border-cyan-500/50 rounded-xl p-4 min-w-[80px] sm:min-w-[100px]">
        <motion.span 
          key={value}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="font-anton text-4xl sm:text-6xl text-cyan-400 block text-center"
          style={{ textShadow: '0 0 20px rgba(0,255,255,0.8)' }}
        >
          {String(value).padStart(2, '0')}
        </motion.span>
      </div>
      {/* Glow effect */}
      <div className="absolute inset-0 bg-cyan-500/20 blur-xl rounded-xl -z-10" />
    </div>
    <span className="font-space text-xs sm:text-sm text-white/60 mt-2 uppercase tracking-widest">{label}</span>
  </div>
);

// Feature card component
const FeatureCard = ({ feature, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.5 + index * 0.2 }}
    whileHover={{ scale: 1.05, y: -5 }}
    className="relative group"
  >
    <div className={`bg-gradient-to-br ${feature.gradient} p-[2px] rounded-2xl`}>
      <div className="bg-black/90 rounded-2xl p-6 h-full">
        <div className="text-5xl mb-4">{feature.icon}</div>
        <h3 className="font-anton text-xl text-white mb-2">{feature.title}</h3>
        <p className="font-space text-sm text-white/60">{feature.description}</p>
      </div>
    </div>
    {/* Hover glow */}
    <div className={`absolute inset-0 bg-${feature.glow}-500/20 blur-2xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity -z-10`} />
  </motion.div>
);

const HypeLanding = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isMuted, setIsMuted] = useState(true);
  const [particles, setParticles] = useState([]);
  const [lang, setLang] = useState('en');
  const [showLangSelector, setShowLangSelector] = useState(false);
  
  // Get current translation
  const t = TRANSLATIONS[lang];

  // Calculate time remaining
  const calculateTimeLeft = useCallback(() => {
    const now = Date.now();
    const difference = TARGET_LAUNCH_TIME - now;

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  }, []);

  // Update countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  // Generate floating particles
  useEffect(() => {
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 10 + 10,
      delay: Math.random() * 5
    }));
    setParticles(newParticles);
  }, []);

  // Pump.fun link
  const PUMP_FUN_LINK = 'https://join.pump.fun/HSag/23v3xzlq';
  
  // Social links
  const TWITTER_LINK = 'https://x.com/slap_arena_coin?s=21';
  const TELEGRAM_LINK = 'https://t.me/DegenSlapSol';

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden flex flex-col items-center justify-center px-4 py-8">
      {/* Language Selector Modal */}
      <AnimatePresence>
        {showLangSelector && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowLangSelector(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#0a0a0a] border-2 border-cyan-500/50 rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
            >
              <h2 className="font-anton text-2xl text-white mb-6 text-center">{t.selectLang}</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {LANGUAGES.map((language) => (
                  <motion.button
                    key={language.code}
                    onClick={() => {
                      setLang(language.code);
                      setShowLangSelector(false);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      lang === language.code
                        ? 'border-cyan-500 bg-cyan-500/20'
                        : 'border-white/10 hover:border-white/30 bg-black/50'
                    }`}
                  >
                    <span className="text-3xl block mb-1">{language.flag}</span>
                    <span className="font-space text-xs text-white/70">{language.name}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Language selector button (top left) */}
      <motion.button
        onClick={() => setShowLangSelector(true)}
        className="fixed top-4 left-4 z-50 flex items-center gap-2 px-3 py-2 rounded-full bg-black/50 border border-white/20 hover:border-cyan-500/50 transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <span className="text-xl">{t.flag}</span>
        <span className="font-space text-xs text-white/70 hidden sm:inline">{TRANSLATIONS[lang].name}</span>
      </motion.button>

      {/* Animated background grid */}
      <div 
        className="fixed inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(#FF00FF15 1px, transparent 1px),
            linear-gradient(90deg, #00FFFF15 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          animation: 'gridMove 20s linear infinite'
        }}
      />

      {/* Floating particles */}
      {particles.map(particle => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-gradient-to-r from-cyan-500 to-pink-500"
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
          }}
          animate={{
            y: [0, -100, 0],
            opacity: [0.2, 0.8, 0.2],
            scale: [1, 1.5, 1]
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
      ))}

      {/* Ambient glow orbs */}
      <div className="fixed top-1/4 -left-20 w-[500px] h-[500px] bg-pink-600/30 rounded-full blur-[150px] pointer-events-none animate-pulse" />
      <div className="fixed bottom-1/4 -right-20 w-[500px] h-[500px] bg-cyan-600/30 rounded-full blur-[150px] pointer-events-none animate-pulse" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-600/10 rounded-full blur-[200px] pointer-events-none" />

      {/* Sound toggle */}
      <motion.button
        onClick={() => setIsMuted(!isMuted)}
        className="fixed top-4 right-4 z-50 p-3 rounded-full bg-black/50 border border-white/20 hover:border-cyan-500/50 transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {isMuted ? <VolumeX className="w-5 h-5 text-white/60" /> : <Volume2 className="w-5 h-5 text-cyan-400" />}
      </motion.button>

      {/* Main content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 max-w-4xl w-full text-center"
      >
        {/* Lock icon */}
        <motion.div
          animate={{ 
            rotate: [0, -5, 5, -5, 0],
            scale: [1, 1.05, 1]
          }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          className="inline-block mb-6"
        >
          <div className="relative">
            <Lock className="w-20 h-20 sm:w-28 sm:h-28 text-cyan-400 mx-auto" strokeWidth={1.5} />
            <div className="absolute inset-0 bg-cyan-500/30 blur-2xl" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="font-anton text-4xl sm:text-6xl lg:text-7xl uppercase tracking-tight mb-4"
          style={{
            background: 'linear-gradient(180deg, #FF0000 0%, #FF00FF 50%, #FFD700 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 60px rgba(255,0,255,0.5)'
          }}
        >
          {t.locked}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="font-space text-base sm:text-lg text-white/70 mb-12 tracking-wider"
        >
          {t.subtitle}
        </motion.p>

        {/* Countdown */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 }}
          className="flex justify-center gap-3 sm:gap-6 mb-16"
        >
          <CountdownDigit value={timeLeft.days} label={t.days} />
          <span className="font-anton text-4xl sm:text-6xl text-cyan-400 self-start mt-4">:</span>
          <CountdownDigit value={timeLeft.hours} label={t.hours} />
          <span className="font-anton text-4xl sm:text-6xl text-cyan-400 self-start mt-4">:</span>
          <CountdownDigit value={timeLeft.minutes} label={t.mins} />
          <span className="font-anton text-4xl sm:text-6xl text-cyan-400 self-start mt-4">:</span>
          <CountdownDigit value={timeLeft.seconds} label={t.secs} />
        </motion.div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="relative group"
          >
            <div className="bg-gradient-to-br from-purple-600 to-pink-600 p-[2px] rounded-2xl">
              <div className="bg-black/90 rounded-2xl p-6 h-full">
                <div className="text-5xl mb-4">🎴</div>
                <h3 className="font-anton text-xl text-white mb-2">{t.packOpening}</h3>
                <p className="font-space text-sm text-white/60">{t.packDesc}</p>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="relative group"
          >
            <div className="bg-gradient-to-br from-red-600 to-orange-600 p-[2px] rounded-2xl">
              <div className="bg-black/90 rounded-2xl p-6 h-full">
                <div className="text-5xl mb-4">⚔️</div>
                <h3 className="font-anton text-xl text-white mb-2">{t.pvp}</h3>
                <p className="font-space text-sm text-white/60">{t.pvpDesc}</p>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="relative group"
          >
            <div className="bg-gradient-to-br from-yellow-500 to-green-500 p-[2px] rounded-2xl">
              <div className="bg-black/90 rounded-2xl p-6 h-full">
                <div className="text-5xl mb-4">💰</div>
                <h3 className="font-anton text-xl text-white mb-2">{t.earn}</h3>
                <p className="font-space text-sm text-white/60">{t.earnDesc}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* GAMEPLAY PREVIEW SCREENSHOTS */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="mt-16 mb-12"
        >
          <h2 className="font-anton text-3xl sm:text-4xl text-white mb-8 tracking-wider">
            🎮 <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-cyan-500">{t.sneakPeek}</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.1 }}
              whileHover={{ scale: 1.05, y: -10 }}
              className="relative group cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative overflow-hidden rounded-2xl border-2 border-white/10 group-hover:border-cyan-500/50 transition-colors">
                <img src={PREVIEW_IMAGES[0].url} alt="Cards" className="w-full h-48 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-anton text-lg text-white">{t.cards}</h3>
                  <p className="font-space text-xs text-white/60">{t.cardsDesc}</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.25 }}
              whileHover={{ scale: 1.05, y: -10 }}
              className="relative group cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative overflow-hidden rounded-2xl border-2 border-white/10 group-hover:border-cyan-500/50 transition-colors">
                <img src={PREVIEW_IMAGES[1].url} alt="Battles" className="w-full h-48 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-anton text-lg text-white">{t.battles}</h3>
                  <p className="font-space text-xs text-white/60">{t.battlesDesc}</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.4 }}
              whileHover={{ scale: 1.05, y: -10 }}
              className="relative group cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative overflow-hidden rounded-2xl border-2 border-white/10 group-hover:border-cyan-500/50 transition-colors">
                <img src={PREVIEW_IMAGES[2].url} alt="Wallet" className="w-full h-48 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-anton text-lg text-white">{t.wallet}</h3>
                  <p className="font-space text-xs text-white/60">{t.walletDesc}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* KEY FEATURES - Wallet, Swap, etc */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 }}
          className="mb-16"
        >
          <h2 className="font-anton text-3xl sm:text-4xl text-white mb-8 tracking-wider">
            ⚡ <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">{t.features}</span>
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {KEY_FEATURES.map((feature, index) => {
              const Icon = feature.icon;
              const colorClasses = {
                cyan: 'border-cyan-500/30 hover:border-cyan-500 text-cyan-400',
                green: 'border-green-500/30 hover:border-green-500 text-green-400',
                purple: 'border-purple-500/30 hover:border-purple-500 text-purple-400',
                yellow: 'border-yellow-500/30 hover:border-yellow-500 text-yellow-400'
              };
              
              return (
                <motion.div
                  key={feature.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.4 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className={`bg-black/60 border-2 ${colorClasses[feature.color]} rounded-xl p-4 text-center transition-all`}
                >
                  <Icon className={`w-8 h-8 mx-auto mb-3 ${colorClasses[feature.color].split(' ').pop()}`} />
                  <h3 className="font-anton text-sm text-white mb-1">{t[feature.key]}</h3>
                  <p className="font-space text-xs text-white/50 leading-relaxed">{t[feature.descKey]}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
        >
          <motion.a
            href={PUMP_FUN_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-green-500 via-emerald-500 to-cyan-500 rounded-2xl font-anton text-xl sm:text-2xl text-black uppercase tracking-wide hover:shadow-[0_0_40px_rgba(16,185,129,0.6)] transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            animate={{
              boxShadow: [
                '0 0 20px rgba(16,185,129,0.4)',
                '0 0 40px rgba(16,185,129,0.6)',
                '0 0 20px rgba(16,185,129,0.4)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            data-testid="buy-slap-btn"
          >
            <Zap className="w-6 h-6" />
            BUY $SLAP ON PUMP.FUN
            <ExternalLink className="w-5 h-5" />
          </motion.a>
        </motion.div>

        {/* Social links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="mt-12 flex justify-center gap-6"
        >
          <motion.a
            href={TWITTER_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-3 bg-black/50 border border-white/20 rounded-xl hover:border-cyan-500/50 hover:bg-black/70 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            <span className="font-space text-sm text-white">TWITTER</span>
          </motion.a>
          
          <motion.a
            href={TELEGRAM_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-3 bg-black/50 border border-white/20 rounded-xl hover:border-cyan-500/50 hover:bg-black/70 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
            <span className="font-space text-sm text-white">TELEGRAM</span>
          </motion.a>
        </motion.div>
      </motion.div>

      {/* CSS for grid animation */}
      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(60px, 60px); }
        }
      `}</style>
    </div>
  );
};

export default HypeLanding;
